import { useMemo, useState, type MouseEvent as ReactMouseEvent } from "react";
import { createPortal } from "react-dom";
import { RowDetailPanel } from "@/components/board/RowDetailPanel";
import { DetailSplitter } from "@/components/board/DetailSplitter";
import { useBoard } from "@/hooks/useBoard";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useDetailPaneRatio } from "@/hooks/useDetailPaneRatio";
import { useDetailViewMode } from "@/hooks/useDetailViewMode";
import { adaptBoard, buildCellWrite, type ViewRow } from "@/lib/boardView";
import { t } from "@/lib/strings";
import { showToast } from "@/lib/toast";
import type { BoardDetail, CellValue, UUID } from "@/lib/types";

// Same helpers BoardPage uses to translate a flat-row patch into cell writes and
// skip no-op writes (kept local — they're tiny + private there).
function readRawCell(
  data: BoardDetail | undefined,
  rowId: string,
  columnId: string,
): CellValue | undefined {
  const row = data?.rows.find((r) => r.id === rowId);
  return row?.cells?.find((c) => c.column_def_id === columnId)?.value;
}

function sameValue(a: CellValue | undefined, b: CellValue | undefined): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/**
 * Opens an existing ticket's RowDetailPanel as an overlay on top of the current
 * screen (e.g. Home), instead of navigating to the board. Self-contained: loads
 * the board via useBoard, adapts it, finds the row, and wires onPatch to the
 * same cell-write mutation the board page uses. Rendered in a portal so it
 * escapes any transformed/clipped ancestor.
 *
 * 🔴 It must wire EVERY control the panel offers, not just the cell writes. Home's "Meine
 * offenen Aufgaben" opens exactly this overlay — and Home is THE surface the Unteraufgaben
 * design nominates for the assignee (the Aufgaben board is hidden from their sidebar). Ship
 * the panel here without `onCreateSubtask`/`onOpenRow` and the section looks alive (checkbox,
 * assignee, Fällig all ride onPatchCell) while "Hinzufügen" and "öffnen" do NOTHING.
 */
export function TicketDetailOverlay({
  boardId,
  rowId,
  onClose,
  focusCommentId,
}: {
  boardId: UUID;
  rowId: UUID;
  onClose: () => void;
  /** Jump-to-comment (①): scroll+flash a comment when this overlay opens on its row. */
  focusCommentId?: string;
}) {
  const board = useBoard(boardId);
  const adapted = useMemo(() => (board.data ? adaptBoard(board.data) : null), [board.data]);

  // "öffnen" on a sub-task swaps the overlay to that row, WITHOUT closing it. The prop stays
  // the source of truth: when the parent opens a different ticket the local override is reset
  // (adjust-state-during-render — no effect, no stale frame).
  const [activeRowId, setActiveRowId] = useState<UUID>(rowId);
  const [seedRowId, setSeedRowId] = useState<UUID>(rowId);
  if (rowId !== seedRowId) {
    setSeedRowId(rowId);
    setActiveRowId(rowId);
  }

  const row = useMemo(
    () => adapted?.rows.find((r) => r.id === activeRowId) ?? null,
    [adapted, activeRowId],
  );

  // Desktop = docked panel + draggable splitter. Mobile keeps the floating,
  // near-full-width RowDetailPanel (its own tap-to-close backdrop) — a docked
  // pane on a phone shrinks the dismiss backdrop to a few px and can push the
  // close button off-screen.
  const isDesktop = useBreakpoint("md");
  // ⑤ presentation preference (desktop only). "large" → the centered modal, which portals
  // its own backdrop, so we skip this overlay's docked-over-page wrapper entirely.
  const [detailView, setDetailView] = useDetailViewMode();
  const largeDetail = detailView === "large" && isDesktop;

  // Draggable width, sharing the same per-user ratio as the board's docked
  // panel (localStorage flow:detail-pane-ratio). The overlay spans the full
  // viewport, so the denominator is window.innerWidth.
  const [detailRatio, setDetailRatio] = useDetailPaneRatio();
  const [resizing, setResizing] = useState(false);
  const startResize = (e: ReactMouseEvent) => {
    e.preventDefault();
    setResizing(true);
    const onMove = (ev: globalThis.MouseEvent) => {
      const w = window.innerWidth;
      if (w <= 0) return;
      setDetailRatio((w - ev.clientX) / w);
    };
    const onUp = () => {
      setResizing(false);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  function onPatch(patch: Partial<ViewRow>) {
    if (!adapted || !board.data) return;
    for (const [field, value] of Object.entries(patch) as [
      keyof ViewRow,
      ViewRow[keyof ViewRow],
    ][]) {
      const write = buildCellWrite(adapted, field, value);
      if (!write) continue;
      const prev = readRawCell(board.data, activeRowId, write.columnId);
      if (sameValue(prev, write.value)) continue;
      board.updateCell.mutate({
        rowId: activeRowId,
        columnDefId: write.columnId,
        value: write.value,
      });
    }
  }

  function onPatchCell(rid: string, columnId: string, value: CellValue) {
    if (!board.data) return;
    const prev = readRawCell(board.data, rid, columnId);
    if (sameValue(prev, value)) return;
    board.updateCell.mutate({ rowId: rid as UUID, columnDefId: columnId as UUID, value });
  }

  // Unteraufgaben: the SAME two steps BoardPage.onCreateSubtask takes — mint the child row,
  // then write its title and (if picked) its assignee. Sorted by position first, so "the first
  // text column" means the column the panel and the table call the title.
  function onCreateSubtask(parentRowId: UUID, title: string, assigneeId: string | null) {
    if (!board.data) return;
    const columns = [...board.data.columns].sort((a, b) => a.position - b.position);
    const titleCol = columns.find((c) => c.type === "text");
    const personCol = columns.find((c) => c.type === "person");
    void board.createRow
      .mutateAsync({ parentRowId })
      .then((created) => {
        if (titleCol) {
          board.updateCell.mutate({
            rowId: created.id as UUID,
            columnDefId: titleCol.id,
            value: title,
          });
        }
        if (assigneeId && personCol) {
          // Person cells are a LIST of user uuids.
          board.updateCell.mutate({
            rowId: created.id as UUID,
            columnDefId: personCol.id,
            value: [assigneeId],
          });
        }
      })
      .catch(() => showToast(t.subtaskCreateFailed, "error"));
  }

  function onOpenRow(rid: string) {
    if (!adapted?.rows.some((r) => r.id === rid)) return;
    setActiveRowId(rid as UUID);
  }

  // Large view: the centered modal portals its own backdrop, so render it directly instead
  // of this overlay's z=200 docked-over-page wrapper (which would otherwise sit above it).
  if (adapted && row && largeDetail) {
    return (
      <RowDetailPanel
        variant="centered"
        onToggleLarge={() => setDetailView("dock")}
        row={row}
        board={adapted}
        onClose={onClose}
        onPatch={onPatch}
        onPatchCell={onPatchCell}
        onCreateSubtask={onCreateSubtask}
        onOpenRow={onOpenRow}
        focusCommentId={focusCommentId}
      />
    );
  }

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        ...(isDesktop ? { display: "flex", flexDirection: "row" } : {}),
      }}
    >
      {adapted && row ? (
        isDesktop ? (
          <>
            {/* Dim backdrop fills the remaining width; click closes. */}
            <div
              onClick={onClose}
              style={{ flex: 1, minWidth: 0, background: "rgba(13,62,99,0.32)" }}
            />
            <DetailSplitter active={resizing} onResizeStart={startResize} />
            <div
              style={{
                width: `${detailRatio * 100}%`,
                minWidth: 360,
                flexShrink: 0,
                minHeight: 0,
                display: "flex",
                background: "var(--surface)",
                // Elevation shadow so the panel clearly floats ABOVE the dimmed
                // page — without it the seam reads as a muddy half-dimmed haze.
                boxShadow: "-14px 0 36px rgba(13,62,99,0.22)",
              }}
            >
              <RowDetailPanel
                variant="docked"
                onToggleLarge={() => setDetailView("large")}
                row={row}
                board={adapted}
                onClose={onClose}
                onPatch={onPatch}
                onPatchCell={onPatchCell}
                onCreateSubtask={onCreateSubtask}
                onOpenRow={onOpenRow}
                focusCommentId={focusCommentId}
              />
            </div>
          </>
        ) : (
          // Mobile: floating, near-full-width panel with its own tap-to-close backdrop.
          <RowDetailPanel
            row={row}
            board={adapted}
            onClose={onClose}
            onPatch={onPatch}
            onPatchCell={onPatchCell}
            onCreateSubtask={onCreateSubtask}
            onOpenRow={onOpenRow}
            focusCommentId={focusCommentId}
          />
        )
      ) : (
        <div
          onClick={onClose}
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(13,62,99,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--hairline)",
              borderRadius: 10,
              padding: "14px 18px",
              fontSize: 13,
              color: "var(--ink-3)",
            }}
          >
            {board.isLoading ? t.loading : t.rowNotFound}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
